import { WebChatWidget } from "@components/ui/WebChatWidget/WebChatWidget"
import { AlertBar } from "@components/ui/AlertBar/AlertBar"
import {
	AppShell,
	Box,
	Flex,
	Group,
	useMantineColorScheme,
	useMantineTheme
} from "@mantine/core"
import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import AppFooter from "@components/navigation/AppFooter"
import AppHeader from "@components/navigation/AppHeader"
import AppMainMenu from "@components/navigation/AppMainMenu"
import styles from "./PageWithNavigation.module.css"
import { useAppShellContext } from "@context/AppShellProvider"
import {
	AEMComponentMapper,
	AEMContent,
	AEMPageType
} from "@AEM/models/AEMPage.model"
import { useAEMComponentData } from "@AEM/hooks/useAEMComponentData"
import { AEMSvg } from "@components/AEMSvg"
import type { ColorScheme } from "@mantine/styles"
import { FocusManagerProvider } from "@context/FocusManager"
import { CountdownBanner } from "@components/ui/CountdownBanner/CountdownBanner"
import { useAEMContent } from "@AEM/hooks/useAEMContent"
import { UseQueryResult } from "@tanstack/react-query"
import { useIsMobile, useIsTablet } from "@utils/device.utils"
import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"

export function PageWithNavigation({
	children
}: {
	children: React.ReactNode
}) {
	const theme = useMantineTheme()
	const { isMenuOpen } = useAppShellContext()
	const [alertBarOpen, setAlertBarOpen] = useState(true)
	const [countdownBannerOpen, setCountdownBannerOpen] = useState(false)
	const { colorScheme } = useMantineColorScheme()
	const location = useLocation()

	const navBarClasses = [styles.NavBar, styles.NavBarWithTransition]
	if (isMenuOpen) navBarClasses.push(styles.NavBarOpen)

	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.APPLICATION
	)

	const { componentData } = useAEMComponentData(
		AEMPageType.LOGIN,
		AEMComponentMapper.LOGIN_FORM
	)

	const { data } = useAEMContent(
		AEMPageType.COUNTDOWN_BANNER
	) as UseQueryResult<AEMContent>
	const subsidiaryLogos = componentData?.additionalLogos

	interface CruiseLineLogo {
		dark: React.ReactNode
		light: React.ReactNode
	}

	const brandLogos: Record<string, CruiseLineLogo> = {
		royal: {
			light: (
				<AEMSvg
					svgData={{ path: subsidiaryLogos?.[0]?.path }}
					w={{ base: 69, sm: 86 }}
					h={{ base: 18, sm: 22 }}
					ariaLabel="Royal Logo"
				/>
			),
			dark: (
				<AEMSvg
					svgData={{
						path: subsidiaryLogos?.[0]?.path,
						iconColor: theme.colors.royal[6]
					}}
					w={{ base: 69, sm: 86 }}
					h={{ base: 18, sm: 22 }}
					ariaLabel="Royal Logo"
				/>
			)
		},
		celebrity: {
			light: (
				<AEMSvg
					svgData={{ path: subsidiaryLogos?.[1]?.path }}
					w={{ base: 86, sm: 106 }}
					h={{ base: 18, sm: 22 }}
					ariaLabel="Celebrity Logo"
				/>
			),
			dark: (
				<AEMSvg
					svgData={{
						path: subsidiaryLogos?.[1]?.path,
						iconColor: theme.colors.royal[6]
					}}
					w={{ base: 86, sm: 106 }}
					h={{ base: 18, sm: 22 }}
					ariaLabel="Celebrity Logo"
				/>
			)
		},
		silversea: {
			light: (
				<AEMSvg
					svgData={{
						path: subsidiaryLogos?.[2]?.path,
						iconColor: theme.colors.royal[4]
					}}
					w={{ base: 70, sm: 92 }}
					h={{ base: 9, sm: 12 }}
					ariaLabel="Silversea Logo"
				/>
			),
			dark: (
				<AEMSvg
					svgData={{
						path: subsidiaryLogos?.[2]?.path,
						iconColor: theme.colors.royal[6]
					}}
					w={{ base: 70, sm: 92 }}
					h={{ base: 9, sm: 12 }}
					ariaLabel="Silversea Logo"
				/>
			)
		}
	}

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [location.pathname])

	const isTablet = useIsTablet()
	const isMobile = useIsMobile()
	const mobileCountdownBannerHeight = countdownBannerOpen ? 147 : 0
	const mobileAlertBarHeight = alertBarOpen
		? isTablet || isMobile
			? 50
			: 100
		: 0
	const mobileHeaderHeight = mobileAlertBarHeight + mobileCountdownBannerHeight

	const desktopCountdownBannerHeight = countdownBannerOpen ? 50 : 0
	const desktopAlertBarHeight = alertBarOpen ? 50 : 0
	const desktopHeaderHeight =
		desktopAlertBarHeight + desktopCountdownBannerHeight

	const countdownBannerComponentData = data?.items
		?.filter(
			(item: AEMContent) =>
				new Date(item.enddate).getTime() > new Date().getTime()
		)
		.sort((a: AEMContent, b: AEMContent) => {
			return new Date(a.enddate).getTime() - new Date(b.enddate).getTime()
		})?.[0]
	const [timeRemaining, setTimeRemaining] = useState({
		days: 0,
		hours: 0,
		minutes: 0,
		seconds: 0
	})
	const calculateTimeRemaining = () => {
		const now = new Date()
		const targetDate = new Date(countdownBannerComponentData?.enddate)
		const timeDifference = targetDate.getTime() - now.getTime()
		const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24))
		const hours = Math.floor(
			(timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
		)
		const minutes = Math.floor(
			(timeDifference % (1000 * 60 * 60)) / (1000 * 60)
		)
		const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000)
		setTimeRemaining({ days, hours, minutes, seconds })
		setCountdownBannerOpen(
			!!countdownBannerComponentData &&
				!!countdownBannerComponentData?.enddate &&
				!!countdownBannerComponentData?.startdate &&
				!!(new Date() > new Date(countdownBannerComponentData?.startdate)) &&
				!!(new Date() < new Date(countdownBannerComponentData?.enddate)) &&
				timeDifference > 0
		)
	}

	useEffect(() => {
		if (countdownBannerComponentData) {
			const intervalId = setInterval(calculateTimeRemaining, 1000)
			return () => {
				clearInterval(intervalId)
			}
		}
	}, [countdownBannerComponentData])

	return (
		<FocusManagerProvider>
			<AppShell
				layout="alt"
				header={{
					height: {
						base: mobileHeaderHeight,
						md: desktopHeaderHeight
					}
				}}
				footer={{ height: { base: "auto" } }}
				navbar={{
					width: isMenuOpen ? 320 : 72,
					breakpoint: "sm"
				}}
				ml={0}
				padding={0}
				transitionDuration={150}
				transitionTimingFunction="ease"
				style={{ display: "flex", flexDirection: "column" }}
			>
				<Flex wrap="nowrap" gap={0} className={styles.TopBar}>
					<Flex direction={{ base: "column", sm: "row" }}>
						<Box style={{ width: "100%" }}>
							{alertBarOpen && (
								<AlertBar
									onClose={() => setAlertBarOpen(false)}
									className={styles.AlertBar}
								/>
							)}
						</Box>
						<Group className={styles.brandIcons}>
							{brandLogos.royal[colorScheme as ColorScheme]}
							{brandLogos.celebrity[colorScheme as ColorScheme]}
							{brandLogos.silversea[colorScheme as ColorScheme]}
						</Group>
					</Flex>
					<Box style={{ width: "100%" }}>
						{countdownBannerOpen && (
							<CountdownBanner
								componentData={countdownBannerComponentData}
								timeRemaining={timeRemaining}
								h={{
									sm: desktopCountdownBannerHeight,
									base: mobileCountdownBannerHeight
								}}
							/>
						)}
					</Box>
				</Flex>
				{/* Skip to main content link - WCAG 2.4.1 (Bypass Blocks) compliance */}
				<button
					type="button"
					id="skip-link"
					className={styles.skipLink}
					onClick={() => {
						const mainContent = document.getElementById("main-content")
						if (mainContent) {
							mainContent.focus()
							mainContent.scrollIntoView({ behavior: "smooth", block: "start" })
						}
					}}
				>
					{dictionary?.accSkipToMainContent ?? ""}
				</button>
				<AppShell.Navbar
					withBorder={false}
					mt={{ base: mobileHeaderHeight, md: desktopHeaderHeight }}
					className={navBarClasses.join(" ")}
				>
					<AppMainMenu
						headerHeight={isMobile ? mobileHeaderHeight : desktopHeaderHeight}
						countdownBannerOpen
					/>
				</AppShell.Navbar>
				<AppHeader mt={{ base: mobileHeaderHeight, md: desktopHeaderHeight }} />
				<AppShell.Main
					className={styles.Main}
					mt={{ base: mobileHeaderHeight + 47, md: desktopHeaderHeight + 70 }}
				>
					<Box className={styles.MainScrollBox}>
						<Box
							id="main-content"
							tabIndex={-1}
							mih={{ base: "calc(100dvh - 612px)", sm: "calc(100dvh - 442px)" }}
							style={{ outline: "none" }}
						>
							{children}
						</Box>
						<AppShell.Footer pos="static">
							<AppFooter useDivider />
						</AppShell.Footer>
					</Box>
				</AppShell.Main>
				<WebChatWidget />
			</AppShell>
		</FocusManagerProvider>
	)
}
